import React, { useEffect, useRef, useState } from 'react';
import {useNavigate, useLocation} from 'react-router-dom';import {toast} from 'sonner';import axios from 'axios';
import {Loader2, RefreshCw} from 'lucide-react';import {Button} from '@/components/ui/button';import {API_URL as API} from '@/utils/apiConfig';
import { useTranslation } from 'react-i18next';
export default function AuthCallback() {
  const {
    t
  } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const exchangeSession = async (sessionId, applyTrial = false) => {
    const refCode = localStorage.getItem('eili_ref_code') || undefined;
    const response = await axios.post(
      `${API}/auth/session`,
      { session_id: sessionId, apply_trial: applyTrial, ref_code: refCode },
      { withCredentials: true }
    );
    localStorage.removeItem('eili_ref_code'); // Clear after use
    return response.data;
  };

  const processSession = async () => {
    const hash = location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sessionId = params.get('session_id');
    // Check if trial was requested (passed via state or URL param)
    const applyTrial = params.get('trial') === 'true' || localStorage.getItem('apply_trial_on_signup') === 'true';
    // Clear the flag
    localStorage.removeItem('apply_trial_on_signup');

    if (!sessionId) {
      // Check if this is an error from Google auth (user cancelled, etc.)
      const errorParam = params.get('error');
      if (errorParam) {
        setError(`Google sign-in was cancelled or failed: ${errorParam}`);
      } else {
        setError('No session ID received from authentication provider.');
      }
      return;
    }

    try {
      const data = await exchangeSession(sessionId, applyTrial);
      const user = data.user;
      toast.success(`Welcome, ${user.name}!`);
      // Small delay to ensure cookie is properly set before navigation
      await new Promise(resolve => setTimeout(resolve, 100));
      // Use replace to prevent back button from returning to auth callback
      window.location.replace('/app');
    } catch (err) {
      console.error('Session exchange error:', err);
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;

      if (status === 429) {
        setError('Too many login attempts. Please wait a few minutes and try again.');
      } else if (detail && typeof detail === 'string') {
        setError(detail);
      } else if (err.code === 'ERR_NETWORK') {
        setError('Network error — could not reach the server. Please check your connection and try again.');
      } else {
        setError('Authentication failed. The session may have expired. Please try signing in again.');
      }
    }
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    processSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = async () => {
    setRetrying(true);
    setError(null);
    await processSession();
    setRetrying(false);
  };

  if (error) {
    return (
      <div className="min-h-dvh flex items-center justify-center px-4" style={{ background: 'var(--theme-background, #0F172A)' }}>
        <div className="text-center max-w-sm" data-testid="auth-error">
          <div
            className="mx-auto mb-4 h-14 w-14 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(239,68,68,0.15)' }}
          >
            <span className="text-2xl" style={{ color: '#ef4444' }}>!</span>
          </div>
          <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--theme-text, #F1F5F9)' }}>
            {t('generated.authcallback_sign_in_issue_0926')}
          </h2>
          <p className="text-sm mb-6" style={{ color: 'var(--theme-textSecondary, #94A3B8)' }}>
            {error}
          </p>
          <div className="flex flex-col gap-2">
            <Button
              onClick={handleRetry}
              disabled={retrying}
              className="w-full rounded-xl py-4 gap-2"
              style={{ background: 'var(--theme-accent, #60A5FA)', color: '#fff' }}
              data-testid="auth-retry-btn"
            >
              {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {t('generated.authcallback_try_again_8267')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="w-full rounded-xl py-4 text-sm"
              style={{ color: 'var(--theme-textSecondary, #94A3B8)' }}
              data-testid="auth-back-btn"
            >
              {t('generated.authcallback_back_to_login_6cb2')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex items-center justify-center" style={{ background: 'var(--theme-background, #0F172A)' }}>
      <div className="text-center">
        <Loader2 className="h-10 w-10 animate-spin mx-auto mb-4" style={{ color: 'var(--theme-accent, #60A5FA)' }} />
        <p className="text-sm" style={{ color: 'var(--theme-textSecondary, #94A3B8)' }}>
          {t('generated.authcallback_completing_authentication_0047')}
        </p>
      </div>
    </div>
  );
}
